import { CheckBoxOutlineBlank, CheckBoxOutlined } from '@mui/icons-material';
import { Box, useTheme } from '@mui/material';
import FluentGrid from '../../Components/FluentGrid';
import TextBlock from '../../Components/Layout/TextBlock';
import ListItem from '../../Components/ListItem';
import ListItemAction from '../../Components/ListItemAction';
import ListItemActions from '../../Components/ListItemActions';
import ListItemChip from '../../Components/ListItemChip';
import ListItemChips from '../../Components/ListItemChips';
import ListItemContent from '../../Components/ListItemContent';
import { Plate } from '../../Components/Plate';
import { LeasmanContractService, LeasmanContractType, LeasmanVehicle, ThemeColor } from '../../system/Domain';
import formatDate from '../../system/formatDate';
import { lasmanContractServiceIsEnsurance } from "./Validation/AssessmentDamages";
import { translateDamageCatalog, translateDepreciationCalculationType } from "../../system/translate";

interface Props {
	leasmanVehicle: LeasmanVehicle
	isSelected: boolean
	onSelect?: (leasmanVehicle?: LeasmanVehicle) => void
}

export const formatLeasmanContractType = (type: LeasmanContractType) => {
	switch (type) {
		case "Mileage":
			return "Kilometervertrag";
		case "ResidualValue":
			return "Restwertvertrag";
		case "External":
			return "Externer Vertrag";
		case "Service":
			return "Servicevertrag";
		case "Supplementary":
			return "Ergänzungsvertrag";
		case "Finance":
			return "Finanzierung";
		case "Installment":
			return "Abzahlungsvereinbarung";
		default:
			return `Vertragstyp: ${type}`;
	}
};

const ServiceTextBlock = ({ service }: { service: LeasmanContractService }) => {
	const theme = useTheme();

	return (
		<TextBlock
			primary={
				<Box
					title={service.type.name}
					sx={{
						...((
							service.type.id === "VE"
							||
							service.type.id === "RF"
						) && {
							color: theme.palette.secondary.main
						}),
						...((
							lasmanContractServiceIsEnsurance(service)
						) && {
							color: theme.palette.primary.main
						})
					}}
				>
					{service.type.name}
				</Box>
			}
			secondary={service.isOpen ? "Service" : "Geschlossener Service"}
			reduced
		/>
	);
};

const LeasmanVehicleListItem = ({ leasmanVehicle, isSelected, onSelect }: Props) => {
	let color: ThemeColor = "info";
	if (isSelected) {
		color = "primary";
	}

	const toggleSelected = () => onSelect?.(isSelected ? undefined : leasmanVehicle);

	const customer = leasmanVehicle.contract.customer;

	return (
		<ListItem color={color}>
			{!!onSelect && (
				<ListItemActions color={color}>
					{!isSelected && (
						<ListItemAction
							icon={<CheckBoxOutlineBlank />}
							onClick={toggleSelected}
						/>
					)}
					{isSelected && (
						<ListItemAction
							icon={<CheckBoxOutlined />}
							onClick={toggleSelected}
						/>
					)}
				</ListItemActions>
			)}
			<ListItemContent color={color} onClick={toggleSelected}>
				<TextBlock
					primary={(
						<Plate plateNumber={leasmanVehicle.plateNumber} style={{ zoom: 0.6 }} />
					)}
					secondary={`${leasmanVehicle.type.make.name} ${leasmanVehicle.type.model.version} ${leasmanVehicle.type.model.variant}`}
				/>
				<Box mt={2} mb={1}>
					<FluentGrid itemWidth={150}>
						<TextBlock
							primary={leasmanVehicle.vin}
							secondary="FIN"
							reduced
						/>
						<TextBlock
							primary={leasmanVehicle.contract.id.toString()}
							secondary="Vertragsnummer"
							reduced
						/>
						<TextBlock
							primary={formatDate(leasmanVehicle.contract.begin.date)}
							secondary="Vertragsbeginn"
							reduced
						/>
						<TextBlock
							primary={formatDate(leasmanVehicle.contract.return.date)}
							secondary="Rückgabedatum"
							reduced
						/>
						<TextBlock
							primary={translateDamageCatalog(leasmanVehicle.contract?.damageCatalog)}
							secondary="Schadenskatalog"
							reduced
						/>
						<TextBlock
							primary={translateDepreciationCalculationType(leasmanVehicle.contract?.calculationType)}
							secondary="Minderwertberechnung"
							reduced
						/>
						{leasmanVehicle.contract.services?.length === 1 && (
							<ServiceTextBlock service={leasmanVehicle.contract.services[0]} />
						)}
					</FluentGrid>
					{leasmanVehicle.contract.services?.length > 1 && (
						<Box mt={2}>
							<FluentGrid itemWidth={150}>
								{leasmanVehicle.contract.services?.sort((a, b) => {
									if (a.isOpen === b.isOpen) return 0;
									return a.isOpen ? -1 : 1;
								})
									.map((service, index) => (
										<ServiceTextBlock service={service} key={index} />
									))}
							</FluentGrid>
						</Box>
					)}
				</Box>
				<ListItemChips>
					<ListItemChip
						color="primary"
						label={leasmanVehicle.tenant}
					/>
					{leasmanVehicle.contract?.businessBranch?.description && (
						<ListItemChip
							color="primary"
							label={leasmanVehicle.contract?.businessBranch?.description}
						/>
					)}
					<ListItemChip
						color="warning"
						label={formatLeasmanContractType(leasmanVehicle.contract.type)}
					/>
					<ListItemChip
						label={customer.hasFrameworkAgreement ? "Rahmenvertrag" : "AGB Vertrag"}
						color={customer.hasFrameworkAgreement ? "primary" : "info"}
						title={customer.hasFrameworkAgreement ? customer.frameworkAgreement : undefined}
					/>
					<ListItemChip
						label={`${customer.name1} ${customer.name2}`.trim()}
					/>
				</ListItemChips>
			</ListItemContent>
		</ListItem>
	);
};

export default LeasmanVehicleListItem;
